import {
  ImageQualityRating,
  EnumOperatorOperationalStatus,
  EnumOperatorPricing,
  EnumTagGroupType,
  PublicationStatusType,
  QueryOperatorManyArgs,
  TagManyQuery,
} from "gql/generated";
import {
  DigitalHealth,
  ListFilters,
  ListFiltersVisibility,
  TagTypes,
  Location,
  MINIMUM_RADIUS,
} from "./filterScreens";
import { Days, Months } from "shared/types";

// Need to typecast `selections` as it comes back as "any"
// Also extend `filledTagGroups` which is saved on the list's `selections`
const getDefaultFiltersFromListSelections = (
  selections: QueryOperatorManyArgs & {
    filter: { filledTagGroups: TagTypes; locationAddress: string };
  },
): ListFilters => {
  const digitalHealth: DigitalHealth[] = [];
  const location: Location = {
    address: "",
    longitude: undefined,
    latitude: undefined,
    radius: MINIMUM_RADIUS,
  };

  if (selections.imageCount?._eq === 0) {
    digitalHealth.push("NO_IMAGES");
  }

  if (selections.filter.contact?.links?.website === false) {
    digitalHealth.push("NO_WEBSITE");
  }

  return {
    filledTagGroups: {
      businessType: selections.filter?.filledTagGroups?.businessType ?? [],
      global: selections.filter?.filledTagGroups?.global ?? [],
      organizationSpecific:
        selections.filter?.filledTagGroups?.organizationSpecific ?? [],
      amenity: selections.filter?.filledTagGroups?.amenity ?? [],
    },
    tags: {
      businessType: selections.filter?.tags?.businessType ?? [],
      global: selections.filter?.tags?.global ?? [],
      organizationSpecific: selections.filter?.tags?.organizationSpecific ?? [],
      amenity: selections.filter?.tags?.amenity ?? [],
    },
    pricing: (selections.filter?.pricing as EnumOperatorPricing[]) ?? [],
    mediaQuality: (selections.avgImageQuality as ImageQualityRating[]) ?? [],
    operationalStatus:
      (selections.filter
        ?.operationalStatus as EnumOperatorOperationalStatus[]) ?? [],
    daysOfOperation: selections.filter?.hoursOfOperation
      ? Object.keys(selections.filter.hoursOfOperation).map(
          (key) => key.toUpperCase() as Days,
        )
      : [],
    seasonality: selections.filter?.seasonality
      ? Object.keys(selections.filter.seasonality).map(
          (key) => key.toUpperCase() as Months,
        )
      : [],
    visibility: (selections.publicationStatus === PublicationStatusType.Approved
      ? "LIVE"
      : selections.publicationStatus === PublicationStatusType.Unpublished
        ? "UNPUBLISHED"
        : "") as ListFiltersVisibility,
    digitalHealth,
    location:
      selections.filter?.locationAddress &&
      selections.filter?.location?.length === 3
        ? {
            address: selections.filter.locationAddress,
            longitude: selections.filter.location[0],
            latitude: selections.filter.location[1],
            radius: selections.filter.location[2],
          }
        : location,
  };
};

const getTagsTagTypeForQueryArgs = (
  selectedTags: TagTypes,
  filledTagGroups: TagTypes,
  fullTags: TagManyQuery["tagMany"],
): TagTypes | undefined => {
  // Get the full tags for each selected businessType tag & filter out
  // any tags where the full tag group has been selected & return an array of
  // the remaining tagIds.
  // This is because when a businessType tagGroup has been selected, it will
  // be included in the operatorMany query under the `selectedTagGroups` field
  const filteredBusinessTypeTagIds = fullTags
    .filter((tag) => tag.tagGroup?.type === EnumTagGroupType.BusinessType)
    .filter((tag) => selectedTags.businessType.includes(tag._id))
    .filter((tag) => !filledTagGroups.businessType.includes(tag.tagGroup?._id))
    .map((tag) => tag._id as string);

  // Only include these tags if they exist. Do not do any further filtering
  const globalTagIds = selectedTags.global.length
    ? selectedTags.global
    : undefined;
  const backOfficeTagIds = selectedTags.organizationSpecific.length
    ? selectedTags.organizationSpecific
    : undefined;
  const amenityTagIds = selectedTags.amenity.length
    ? selectedTags.amenity
    : undefined;

  return filteredBusinessTypeTagIds.length ||
    (globalTagIds && globalTagIds.length) ||
    (backOfficeTagIds && backOfficeTagIds.length) ||
    (amenityTagIds && amenityTagIds.length)
    ? ({
        businessType: filteredBusinessTypeTagIds.length
          ? filteredBusinessTypeTagIds
          : undefined,
        global: globalTagIds,
        organizationSpecific: backOfficeTagIds,
        amenity: amenityTagIds,
      } as TagTypes)
    : undefined;
};

export { getDefaultFiltersFromListSelections, getTagsTagTypeForQueryArgs };
